import React, { useState, useMemo } from "react";
import {
  ToolShell, Field, Select, Result, fmt,
  SpecCard, SpecValue,
} from "./_shared";
import {
  selectCableSize, selectBreaker,
  INSTALL_METHODS, ambientFactor,
} from "./_electrical";

/**
 *  CABLE SIZE CALCULATOR
 *  --------------------------------------------------------------
 *  Selects the smallest copper PVC cable that satisfies BOTH:
 *    1. Ampacity ≥ design current  (after installation + ambient derating)
 *    2. Voltage drop ≤ user limit  (mV/A/m × I × L)
 *
 *  Source: SANS 10142-1 / IEC 60364-5-52 tables.  This is a planning
 *  aid — the final installation must be certified by a registered
 *  electrician issuing a CoC.
 *  --------------------------------------------------------------
 */
export default function CableSizeCalculator() {
  // Input mode — sometimes the user knows kW, sometimes amps.
  const [inputMode, setInputMode] = useState("kw");        // 'kw' | 'amps'
  const [system, setSystem] = useState("3p400");           // 1p230 | 3p400 | custom
  const [customV, setCustomV] = useState(48);

  const [kw, setKw] = useState(15);
  const [pf, setPf] = useState(0.85);
  const [amps, setAmps] = useState(25);

  const [length, setLength] = useState(50);
  const [installMethod, setInstallMethod] = useState("conduit-wall");
  const [ambientC, setAmbientC] = useState(35);
  const [vdMaxPct, setVdMaxPct] = useState(5);
  const [marginPct, setMarginPct] = useState(25);

  // Derived
  const threePhase = system === "3p400";
  const voltage = system === "1p230" ? 230
                : system === "3p400" ? 400
                : customV;
  const vFactor = threePhase ? Math.sqrt(3) : 1;
  const designI = useMemo(() => {
    if (inputMode === "amps") return amps;
    if (!kw || !voltage || !pf) return 0;
    return (kw * 1000) / (vFactor * voltage * pf);
  }, [inputMode, kw, amps, voltage, pf, vFactor]);

  const sizingI = designI * (1 + marginPct / 100);
  const breaker = selectBreaker(sizingI);
  const installFactor = INSTALL_METHODS.find((m) => m.id === installMethod)?.factor ?? 1;
  const ambFactor = ambientFactor(ambientC);

  const cable = selectCableSize({
    current_A: sizingI,
    length_m: length,
    threePhase,
    voltage,
    vdMaxPct,
    installFactor,
    ambientFact: ambFactor,
  });

  // Status colouring for the cable result.
  const cableStatus = !cable.size ? "alert"
                    : cable.byAmpacity && cable.byVdrop && cable.byAmpacity !== cable.byVdrop ? "warn"
                    : "ok";
  const cableNote = !cable.size
    ? "Single cable cannot carry this load — split into parallel runs or step up the voltage."
    : cable.byAmpacity > cable.byVdrop
    ? `Driven by ampacity, not voltage drop (voltage drop alone needs only ${cable.byVdrop} mm²).`
    : cable.byVdrop > cable.byAmpacity
    ? `Driven by voltage drop, not ampacity (ampacity alone needs only ${cable.byAmpacity} mm²).`
    : "Meets both ampacity and voltage-drop limits.";

  return (
    <ToolShell
      slug="cable-size"
      title="Cable size calculator"
      oneLiner="Select the smallest copper cable that meets both ampacity and voltage-drop limits per SANS 10142-1 / IEC 60364, with installation-method and ambient-temperature derating."
      onExport={() => ({
        inputs: [
          { label: "Load input mode", value: inputMode === "kw" ? "by power (kW)" : "by current (A)" },
          { label: "Voltage system", value: system === "1p230" ? "Single phase 230 V"
                                          : system === "3p400" ? "Three phase 400 V"
                                          : `Custom DC / AC · ${voltage} V` },
          ...(inputMode === "kw" ? [
            { label: "Power", value: kw, unit: "kW" },
            { label: "Power factor", value: pf },
          ] : [
            { label: "Current", value: amps, unit: "A" },
          ]),
          { label: "Cable run length", value: length, unit: "m" },
          { label: "Installation method", value: INSTALL_METHODS.find((m) => m.id === installMethod)?.label ?? installMethod },
          { label: "Ambient temperature", value: ambientC, unit: "°C" },
          { label: "Max voltage drop", value: vdMaxPct, unit: "%" },
          { label: "Sizing margin", value: marginPct, unit: "%" },
        ],
        results: [
          { label: "Design current",         value: fmt(designI, 1), unit: "A" },
          { label: "Sizing current",          value: fmt(sizingI, 1), unit: `A (with ${marginPct} % margin)` },
          { label: "Suggested breaker",       value: breaker ? `${breaker} A` : "above 630 A" },
          { label: "Minimum cable size",      value: cable.size ? `${cable.size} mm²` : "above 300 mm²",
            status: cableStatus, note: cableNote },
          { label: "Actual voltage drop",     value: cable.size ? fmt(cable.vDrop_pct, 2) : "—",
            unit: cable.size ? `% over ${length} m` : "" },
          { label: "Derated ampacity (at chosen size)",
            value: cable.size ? fmt(cable.ampacity, 1) : "—",
            unit: cable.size ? "A" : "" },
        ],
        notes: [
          "Standard reference: SANS 10142-1 / IEC 60364-5-52 — single-core copper PVC at 70 °C insulation.",
          "Ampacity derated by installation method × ambient factor.  Voltage drop computed from mV/A/m tables at PF 0.85.",
          "Planning aid — final installation must be certified by a registered installation electrician.",
        ],
        spec: cable.size ? {
          title: "Cable specification",
          body: `For a <strong>${inputMode === "kw" ? `${kw} kW` : `${amps} A`}</strong> load over a <strong>${length} m</strong> run on a <strong>${system === "1p230" ? "230 V single-phase" : system === "3p400" ? "400 V three-phase" : `${voltage} V`}</strong> system, install <strong>${cable.size} mm² copper</strong> (PVC, 70 °C) protected by a <strong>${breaker ? `${breaker} A` : "≥ 630 A"}</strong> breaker. Voltage drop at full load = <strong>${fmt(cable.vDrop_pct, 2)} %</strong> (limit ${vdMaxPct} %). Derated ampacity at this size = <strong>${fmt(cable.ampacity, 0)} A</strong>.`,
        } : {
          title: "Cable specification",
          body: `A single cable cannot carry <strong>${fmt(sizingI, 1)} A</strong> over <strong>${length} m</strong> within the ${vdMaxPct} % voltage-drop limit. Split into parallel runs, step up the voltage, or shorten the run.`,
        },
      })}
    >
      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Voltage system" span={2}>
          <Select value={system} onChange={setSystem} options={[
            { value: "1p230", label: "Single phase 230 V AC" },
            { value: "3p400", label: "Three phase 400 V AC" },
            { value: "custom", label: "Custom (DC bus / 110 V / 525 V / 690 V)" },
          ]} testid="cs-system" />
        </Field>
        {system === "custom" && (
          <Field label="Custom voltage" span={2}>
            <SuffixNum value={customV} onChange={setCustomV} suffix="V" step="1" testid="cs-customv" />
          </Field>
        )}

        <Field label="Load input">
          <Select value={inputMode} onChange={setInputMode} options={[
            { value: "kw",   label: "By power (kW)" },
            { value: "amps", label: "By current (A)" },
          ]} testid="cs-mode" />
        </Field>

        {inputMode === "kw" ? (
          <>
            <Field label="Power">
              <SuffixNum value={kw} onChange={setKw} suffix="kW" step="0.1" testid="cs-kw" />
            </Field>
            <Field label="Power factor" hint="0.85 typical for VSD-driven motors · 1.00 for resistive loads.">
              <SuffixNum value={pf} onChange={setPf} suffix="PF" step="0.01" testid="cs-pf" />
            </Field>
          </>
        ) : (
          <Field label="Current">
            <SuffixNum value={amps} onChange={setAmps} suffix="A" step="0.5" testid="cs-amps" />
          </Field>
        )}

        <Field label="Cable run length" hint="One-way distance from supply to load.">
          <SuffixNum value={length} onChange={setLength} suffix="m" step="1" testid="cs-len" />
        </Field>
        <Field label="Installation method" hint="Each method has its own ampacity table — affects allowable current.">
          <Select value={installMethod} onChange={setInstallMethod}
            options={INSTALL_METHODS.map((m) => ({ value: m.id, label: m.label }))}
            testid="cs-install" />
        </Field>
        <Field label="Ambient temperature" hint="PVC cable derates above 30 °C.">
          <SuffixNum value={ambientC} onChange={setAmbientC} suffix="°C" step="1" testid="cs-ambient" />
        </Field>
        <Field label="Max voltage drop" hint="SANS 10142 allows ≤ 5 % from supply to point of use (3 % preferred for motor circuits).">
          <SuffixNum value={vdMaxPct} onChange={setVdMaxPct} suffix="%" step="0.5" testid="cs-vd" />
        </Field>
        <Field label="Sizing margin" hint="Extra capacity on top of design current for breaker and cable selection — typically 25 %.">
          <SuffixNum value={marginPct} onChange={setMarginPct} suffix="%" step="5" testid="cs-margin" />
        </Field>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <Result label="Design current"
          primary={{ value: fmt(designI, 1), unit: "A" }}
          alts={[{ value: fmt(sizingI, 1), unit: `A with ${marginPct} % margin` }]}
          testid="cs-result-current" />
        <Result label="Suggested breaker"
          primary={{ value: breaker ? `${breaker}` : "—", unit: "A" }}
          note={breaker
            ? `${threePhase ? "3-pole" : "1-pole"} MCB / MCCB · ${voltage} V system.`
            : "Beyond standard MCB range."}
          testid="cs-result-breaker" />
        <Result label="Minimum cable size"
          primary={{ value: cable.size ? `${cable.size}` : "—", unit: "mm² copper" }}
          alts={cable.size ? [{ value: fmt(cable.vDrop_pct, 2), unit: `% Vd over ${length} m` }] : null}
          status={cableStatus} note={cableNote}
          testid="cs-result-cable" />
        <Result label="Derated ampacity at chosen size"
          primary={{ value: cable.size ? fmt(cable.ampacity, 0) : "—", unit: cable.size ? "A" : "" }}
          note={cable.size ? `After ${(installFactor * 100).toFixed(0)} % install × ${(ambFactor * 100).toFixed(0)} % ambient.` : null}
          testid="cs-result-ampacity" />
      </div>

      {cable.size ? (
        <SpecCard
          eyebrow="Cable specification"
          testid="cs-spec-card"
          footer={cableNote}
        >
          For a <SpecValue>{inputMode === "kw" ? `${kw} kW` : `${amps} A`}</SpecValue> load over a{" "}
          <SpecValue>{length} m</SpecValue> run on a{" "}
          <SpecValue>{system === "1p230" ? "230 V single-phase" : system === "3p400" ? "400 V three-phase" : `${voltage} V`}</SpecValue> system,
          install <SpecValue>{cable.size} mm² copper</SpecValue> (PVC, 70 °C) protected by a{" "}
          <SpecValue>{breaker ? `${breaker} A` : "≥ 630 A"}</SpecValue> breaker. Voltage drop at full load ={" "}
          <SpecValue>{fmt(cable.vDrop_pct, 2)} %</SpecValue> (limit {vdMaxPct} %). Derated ampacity at this size ={" "}
          <SpecValue>{fmt(cable.ampacity, 0)} A</SpecValue>.
        </SpecCard>
      ) : (
        <SpecCard eyebrow="Cable specification" testid="cs-spec-card">
          A single cable cannot carry <SpecValue>{fmt(sizingI, 1)} A</SpecValue> over{" "}
          <SpecValue>{length} m</SpecValue> within the {vdMaxPct} % voltage-drop limit. Split into parallel runs, step up the voltage, or shorten the run.
        </SpecCard>
      )}
    </ToolShell>
  );
}

function SuffixNum({ value, onChange, suffix, step = "any", testid }) {
  // Buffered text-mode input — see SuffixNum (PurificationPlantSizing.jsx).
  const numeric = Number(value);
  const externalStr = Number.isFinite(numeric) && numeric !== 0 ? String(numeric) : "";
  const [buf, setBuf] = React.useState(externalStr);
  React.useEffect(() => {
    const parsed = parseFloat(buf.replace(",", "."));
    const same = Number.isFinite(parsed)
      ? Math.abs(parsed - numeric) < 1e-9
      : (buf === "" && numeric === 0);
    if (!same) setBuf(externalStr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <div className="flex items-stretch">
      <input type="text" inputMode="decimal" value={buf} placeholder="0"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw !== "" && !/^-?\d*[.,]?\d*$/.test(raw)) return;
          setBuf(raw);
          if (raw === "" || raw === "-" || raw === "." || raw === ",") { onChange(0); return; }
          const n = parseFloat(raw.replace(",", "."));
          if (Number.isFinite(n)) onChange(n);
        }}
        className="flex-1 min-w-0 px-3 py-2.5 bg-white border outline-none focus:border-[#1FB6C1]"
        style={{ borderColor: "rgba(12,42,64,0.16)", color: "#0C2A40", fontSize: "0.95rem", fontWeight: 500 }}
        data-testid={testid} />
      <span className="inline-flex items-center px-3 border border-l-0 font-mono"
        style={{ background: "#FAFAF7", borderColor: "rgba(12,42,64,0.16)", color: "rgba(12,42,64,0.65)", fontSize: "0.72rem" }}>
        {suffix}
      </span>
    </div>
  );
}
